<template>
  <q-editor
    ref="editorRef"
    v-bind="$attrs"
    v-model="model"
    min-height="5rem"
    paragraph-tag="p"
    content-class="tw-prose"
    :toolbar="defaultToolbar"
    :definitions="definitions"
    @paste="onPaste"
    @keydown="onKeydown"
    @focus="focusInput"
  />
</template>

<script setup>
import {
  editorBold,
  editorCode,
  editorImage,
  editorItalic,
  editorLink,
  editorOrdered,
  editorUnderline,
  editorUnlink,
  editorUnordered,
  toDuotoneIcon,
  toClassicIcon,
} from "common/defaultIcons";
import { UPLOAD_LIMITS_MB } from "shared/constants";
import { useQuasar } from "quasar";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  nextTick,
  watch,
} from "vue";

// Quasar
const $q = useQuasar();

const model = defineModel({
  type: String,
  default: "",
  required: true,
});

// Refs
const editorRef = useTemplateRef("editorRef");

const props = defineProps({
  headings: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Boolean,
    default: false,
  },
  link: {
    type: Boolean,
    default: false,
  },
  code: {
    type: Boolean,
    default: false,
  },
  minimal: {
    type: Boolean,
    default: false,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
});

const defaultToolbar = computed(() => {
  if (props.minimal) {
    return [["bold", "italic", "underline"]];
  }

  return [
    [
      {
        label: $q.lang.editor.align,
        icon: "fa-duotone fa-align-left",
        fixedLabel: true,
        list: "only-icons",
        options: ["left", "center", "right", "justify"],
      },
      ...(props.headings
        ? [
            {
              label: "Titel",
              options: ["p", "h1", "h2", "h3"],
            },
          ]
        : []),
    ],
    ["bold", "italic", "underline"],
    ["unordered", "ordered"],
    ...(props.code ? [["code"]] : []),
    ...(props.link ? [["link", "unlink"]] : []),
    ...(props.images ? [["images"]] : []),
  ];
});

const uploadImage = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";

  input.onchange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const maxSize = UPLOAD_LIMITS_MB.EDITOR_INLINE_IMAGE * 1024 * 1024;
    if (file.size > maxSize) {
      $q.notify({
        type: "negative",
        message: `Afbeelding is te groot. Maximaal toegestane grootte is ${UPLOAD_LIMITS_MB.EDITOR_INLINE_IMAGE}MB.`,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      const imageUrl = e.target.result;
      insertImageToEditor(imageUrl);
    };
    reader.readAsDataURL(file);
  };

  input.click();
};

const insertImageToEditor = (src) => {
  const edit = editorRef.value;
  edit.caret.restore();
  edit.runCmd("insertHTML", `<img src="${src}" style="max-width: 100%;" />`);
  edit.focus();
};

const definitions = computed(() => {
  return {
    images: {
      icon: toDuotoneIcon(editorImage),
      tip: "Afbeelding invoegen",
      handler: uploadImage,
    },
    bold: {
      icon: toClassicIcon(editorBold, "solid"),
    },
    italic: {
      icon: toClassicIcon(editorItalic, "solid"),
    },
    underline: {
      icon: toClassicIcon(editorUnderline, "solid"),
    },
    unordered: {
      icon: toDuotoneIcon(editorUnordered),
    },
    ordered: {
      icon: toDuotoneIcon(editorOrdered),
    },
    link: {
      icon: toDuotoneIcon(editorLink),
      tip: "Link toevoegen",
      handler: () => {
        addLinkHandler();
      },
    },
    unlink: {
      icon: toDuotoneIcon(editorUnlink),
      tip: "Link verwijderen",
      handler: () => {
        removeLink();
      },
    },
    code: {
      icon: toClassicIcon(editorCode, "solid"),
    },
  };
});

const removeLink = () => {
  const edit = editorRef.value;
  if (!edit) {
    return;
  }

  edit.focus();
  edit.runCmd("unlink");
};

const addLinkHandler = () => {
  const edit = editorRef.value;

  const selection = window.getSelection();
  let savedRange = null;
  let selectedText = "";
  let selectedHref = "";

  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    savedRange = range.cloneRange();
    selectedText = selection
      .toString()
      .replace(/\u200B/g, "")
      .trim();

    let node = selection.anchorNode;
    while (node && node.nodeType === 3) {
      node = node.parentNode;
    }
    const anchor = node?.closest?.("a");
    if (anchor && anchor.href) {
      selectedHref = anchor.href;
    }
  }

  const isValidUrl = (text) => {
    try {
      const url = new URL(text);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const initialUrl = isValidUrl(selectedHref)
    ? selectedHref
    : isValidUrl(selectedText)
      ? selectedText
      : "https://";

  const initialTitle = selectedText || "";

  $q.dialog({
    title: "Voer de URL in",
    message: "Plak of typ de URL voor de link:",
    prompt: {
      model: initialUrl,
      type: "text",
      label: "URL",
      isValid: (val) => isValidUrl(val),
      rules: [
        (val) => !!val || "URL is verplicht",
        (val) => isValidUrl(val) || "URL moet geldig zijn (http/https)",
      ],
    },
    cancel: true,
    persistent: true,
  }).onOk((url) => {
    $q.dialog({
      title: "Linktitel",
      message: "Tekst die zichtbaar moet zijn als link:",
      prompt: {
        model: initialTitle,
        type: "text",
        label: "Titel",
      },
      cancel: true,
      persistent: true,
    }).onOk((title) => {
      setTimeout(() => {
        edit.focus();

        if (savedRange) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange);

          savedRange.deleteContents();
        }

        const safeTitle = title.replace(/"/g, "&quot;");
        edit.runCmd(
          "insertHTML",
          `<a href="${url}" target="_blank" rel="noopener noreferrer">${safeTitle}</a>`,
        );

        edit.focus();
      }, 0);
    });
  });
};

// A single-line editor may never grow a second line. Enter is swallowed on the
// content element only, so the toolbar buttons stay operable by keyboard, and a
// pasted newline arrives as a space instead of a break.
const onKeydown = (evt) => {
  if (props.singleLine === false || evt.key !== "Enter") {
    return;
  }

  if (evt.target === editorRef.value?.getContentEl()) {
    evt.preventDefault();
  }
};

const flattenPaste = (text) =>
  props.singleLine === true ? text.replace(/\s*[\r\n]+\s*/g, " ") : text;

const onPaste = (evt) => {
  // Let inputs do their thing, so we don't break pasting of links.
  if (evt.target.nodeName === "INPUT") {
    return;
  }
  let text, onPasteStripFormattingIEPaste;
  evt.preventDefault();
  evt.stopPropagation();
  if (evt.originalEvent && evt.originalEvent.clipboardData?.getData) {
    text = evt.originalEvent.clipboardData.getData("text/plain");
    editorRef.value.runCmd("insertText", flattenPaste(text));
  } else if (evt.clipboardData?.getData) {
    text = evt.clipboardData.getData("text/plain");
    editorRef.value.runCmd("insertText", flattenPaste(text));
  } else if (window.clipboardData?.getData) {
    if (!onPasteStripFormattingIEPaste) {
      onPasteStripFormattingIEPaste = true;
      editorRef.value.runCmd("ms-pasteTextOnly", text);
    }
    onPasteStripFormattingIEPaste = false;
  }
};

/* -------------------------------------------
   Ensure the editor always starts with <p>…</p>
   and wraps any leading inline/text nodes into <p>.
-------------------------------------------- */

const INLINE_TAGS = new Set([
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "SPAN",
  "A",
  "CODE",
  "S",
  "DEL",
  "SUB",
  "SUP",
  "SMALL",
  "MARK",
  "IMG",
]);

let stopObserving = null;
let stopParagraphObserver = null;

// Remove inline font-size from any node inside root; return true if changes were made
const stripInlineFontSizes = (rootEl) => {
  if (!rootEl) {
    return false;
  }
  let changed = false;
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node) {
    const el = node;
    if (el.style && el.style.fontSize) {
      el.style.removeProperty("font-size");
      // Clean empty style attributes
      if (el.style.cssText.trim() === "") {
        el.removeAttribute("style");
      }
      changed = true;
    } else if (el.hasAttribute && el.hasAttribute("style")) {
      // Fallback: if style attribute contains font-size in raw text (edge cases)
      const styleText = el.getAttribute("style") || "";
      if (/font-size\s*:/i.test(styleText)) {
        // Remove only the font-size declaration
        const cleaned = styleText
          .split(";")
          .map((s) => s.trim())
          .filter((s) => s.length > 0 && !/font-size\s*:/i.test(s))
          .join("; ");
        if (cleaned.length > 0) {
          el.setAttribute("style", cleaned);
        } else {
          el.removeAttribute("style");
        }
        changed = true;
      }
    }
    node = walker.nextNode();
  }
  return changed;
};

const normalizeFirstBlock = (editorElement) => {
  if (!editorElement) {
    return;
  }

  // If already starts with a block <p>, we're good
  const firstNode = editorElement.firstChild;
  if (firstNode?.nodeType === Node.ELEMENT_NODE) {
    const el = firstNode;
    // If it's already a paragraph, do nothing
    if (el.tagName === "P") {
      return;
    }

    // If it's inline (e.g., <strong>Test</strong>), wrap the run of inline/text nodes into <p>
    if (INLINE_TAGS.has(el.tagName)) {
      const p = document.createElement("p");
      while (
        editorElement.firstChild &&
        (editorElement.firstChild.nodeType === Node.TEXT_NODE ||
          (editorElement.firstChild.nodeType === Node.ELEMENT_NODE &&
            INLINE_TAGS.has(editorElement.firstChild.tagName)))
      ) {
        p.appendChild(editorElement.firstChild);
      }
      if (p.innerHTML === "") {
        p.innerHTML = "<br>";
      }
      editorElement.insertBefore(p, editorElement.firstChild);
      model.value = editorElement.innerHTML;
      return;
    }
    // If it’s a different block (like H1) and you want *strictly* <p> first, wrap only if it starts with text/inline.
    return;
  }

  // Starts with a text node -> wrap the leading text/inline sequence into <p>
  if (firstNode?.nodeType === Node.TEXT_NODE) {
    const p = document.createElement("p");
    while (
      editorElement.firstChild &&
      (editorElement.firstChild.nodeType === Node.TEXT_NODE ||
        (editorElement.firstChild.nodeType === Node.ELEMENT_NODE &&
          INLINE_TAGS.has(editorElement.firstChild.tagName)))
    ) {
      p.appendChild(editorElement.firstChild);
    }
    if (p.innerHTML === "") {
      p.innerHTML = "<br>";
    }
    editorElement.insertBefore(p, editorElement.firstChild);
    model.value = editorElement.innerHTML;
  }
};

const observeEditorAnchors = (editorElement, model) => {
  const updateModelFromEditor = () => {
    model.value = editorElement.innerHTML;
  };

  const observer = new MutationObserver(() => {
    const anchors = editorElement.querySelectorAll("a");
    let changed = false;

    anchors.forEach((anchor) => {
      if (
        !anchor.hasAttribute("target") ||
        anchor.getAttribute("target") !== "_blank"
      ) {
        anchor.setAttribute("target", "_blank");
        changed = true;
      }
      if (
        !anchor.hasAttribute("rel") ||
        anchor.getAttribute("rel") !== "noopener noreferrer"
      ) {
        anchor.setAttribute("rel", "noopener noreferrer");
        changed = true;
      }
    });

    // Strip inline font-size that may be introduced after link insertions
    if (stripInlineFontSizes(editorElement)) {
      changed = true;
    }

    if (changed) {
      updateModelFromEditor();
    }
  });

  observer.observe(editorElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return () => observer.disconnect();
};

const focusInput = () => {
  const editorEl = editorRef.value.getContentEl();

  // Seed an empty paragraph if needed
  if (!model.value || !model.value.trim()) {
    model.value = "<p><br></p>";

    // write into DOM immediately so caret is inside <p>
    editorEl.innerHTML = model.value;
  }
};

onMounted(async () => {
  await nextTick();

  if (!editorRef.value) {
    return;
  }

  const editorEl = editorRef.value.getContentEl();

  // Normalize once on mount
  normalizeFirstBlock(editorEl);

  // Initial sanitize to remove any inline font-size present in initial value
  if (stripInlineFontSizes(editorEl)) {
    model.value = editorEl.innerHTML;
  }

  // Existing observer for anchors
  stopObserving = observeEditorAnchors(editorEl, model);

  // Observer to keep first block normalized
  let lock = false;
  const paragraphObserver = new MutationObserver(() => {
    if (lock) {
      return;
    }
    lock = true;
    normalizeFirstBlock(editorEl);
    // small defer to avoid thrash
    setTimeout(() => (lock = false), 0);
  });

  paragraphObserver.observe(editorEl, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  stopParagraphObserver = () => paragraphObserver.disconnect();

  // Also normalize when model changes programmatically
  watch(
    () => model.value,
    () => {
      normalizeFirstBlock(editorEl);
    },
  );
});

onBeforeUnmount(() => {
  if (stopObserving) {
    stopObserving();
  }
  if (stopParagraphObserver) {
    stopParagraphObserver();
  }
});
</script>

<style lang="scss">
.q-editor {
  @apply tw-rounded-lg;

  &__content {
    @apply tw-prose;

    a {
      @apply tw-cursor-text;
    }
  }
}
</style>
